import { reaction } from 'mobx'
import { DisposerTracker } from '../../util/DisposerTracker'
import type { MeetingCubit } from '../MeetingCubit'

type PendingVerification = {
  startedAt: number
  duration: number
  // Latest moment a server confirmation can still count for this utterance.
  windowEnd: number
  timer?: ReturnType<typeof setTimeout>
}

// Server confirmations we keep for matching against pending utterances. Bounded
// by count rather than by age: an utterance can run long and its window reaches
// back to its start, so an age cutoff would have to be tied to the longest
// utterance we might still be holding. Confirmations tick a few times a second
// while the user speaks, so this covers about a minute of continuous speech.
const MAX_TRACKED_CONFIRMATIONS = 200

/**
 * Verifies that speech detected by the local microphone actually reaches the
 * server. The raw local mic analyser (`meeting_user_spoke_locally`) can fire
 * while the LiveKit server never registers the user as an active speaker
 * (`meeting_user_spoke`) - meaning their audio is not making it to the SFU and
 * nobody can hear them. This sits one layer below the transcription watcher in
 * `TranscriptController`.
 *
 * Model: every un-muted local utterance schedules a short verification timer.
 * On expiry we check whether the server confirmed the user as speaking during
 * (or shortly after) that utterance, and report the answer on
 * `speechReachabilityProblem` / `speechReachabilitySuccess`.
 *
 * Those are its own events rather than the `transcriptProblem` pair the
 * transcription watcher uses, because the two faults are not the same fault.
 * A broken transcriber still leaves the user registered as an active speaker,
 * so reachability would confirm every utterance and, on a shared channel, keep
 * clearing a transcript problem that is still happening. Nothing listens to
 * these yet - they carry the analytics until the confirmed/unconfirmed split
 * has been seen in production.
 */
export class SpeechReachabilityController {
  meeting: MeetingCubit

  private disposers = new DisposerTracker()
  private serverConfirmations: number[] = []
  private pending = new Set<PendingVerification>()
  private hasPendingProblem = false

  constructor(meeting: MeetingCubit) {
    this.meeting = meeting
  }

  initialize() {
    // The flag is the kill switch for a bad rollout, so switching it off has
    // to mean "stop now", not "stop once the work already in flight drains".
    // Two things outlive the flip without this: timers armed while it was on,
    // and a problem raised under it. The problem is the one a timer cannot
    // clean up - nothing calls `verify` again once the flag is off, so a
    // recovery event would otherwise pair a success with a problem from a
    // rollout that ended hours earlier.
    this.disposers.add(
      reaction(
        () => this.enabled,
        (enabled) => {
          if (!enabled) this.reset()
        }
      )
    )
  }

  dispose() {
    this.disposers.dispose()
    this.reset()
    this.serverConfirmations = []
  }

  /**
   * Drop everything a verdict could be built from, short of the confirmation
   * history. Confirmations are kept: they record that audio did reach the SFU
   * at a moment in time, which stays true whether or not we are watching, and
   * an utterance running across the flag coming back on is entitled to the
   * ones earned during it.
   */
  private reset() {
    for (const entry of this.pending) clearTimeout(entry.timer)
    this.pending.clear()
    this.hasPendingProblem = false
  }

  private get enabled(): boolean {
    return this.meeting.repository.featureFlags.data
      .spokeLocallyVerificationEnabled
  }

  // Delay before checking an utterance, streamed via feature flags so it can be
  // tuned in production without a deploy.
  private get verificationDelayMS(): number {
    return this.meeting.repository.featureFlags.data
      .spokeLocallyVerificationDelayMS
  }

  /**
   * Called whenever the LiveKit server flags the local user as an active
   * speaker - proof that their audio is reaching the SFU.
   */
  recordServerConfirmedSpeaking() {
    this.serverConfirmations.push(Date.now())
    if (this.serverConfirmations.length > MAX_TRACKED_CONFIRMATIONS) {
      this.serverConfirmations.shift()
    }
  }

  /**
   * Called when the local mic analyser detects the end of an utterance.
   *
   * Only a fully un-muted utterance is measured. A mute transition part way
   * through means some of the speech was never published, so a missing server
   * confirmation says nothing about whether audio can reach the SFU, and
   * counting it would put noise in the very split this watcher exists to read.
   */
  recordLocalSpeech(
    duration: number,
    wasMutedOnStart: boolean,
    isMutedAtEnd: boolean
  ) {
    if (!this.enabled) return
    if (wasMutedOnStart || isMutedAtEnd) return

    const endedAt = Date.now()
    const startedAt = endedAt - duration

    // A confirmation that arrives after the next utterance has begun belongs to
    // that utterance, not to one already waiting. Close the older windows here,
    // otherwise a reachable utterance clears every unreachable one before it -
    // which is the exact shape of intermittent reachability this watcher is for.
    for (const entry of this.pending) {
      entry.windowEnd = Math.min(entry.windowEnd, startedAt)
    }

    // Unclamped, the window closes at the moment we check, so waiting longer
    // still means being more forgiving.
    const entry: PendingVerification = {
      startedAt,
      duration,
      windowEnd: endedAt + this.verificationDelayMS,
    }

    entry.timer = setTimeout(() => {
      this.pending.delete(entry)
      this.verify(entry)
    }, this.verificationDelayMS)

    this.pending.add(entry)
  }

  private verify({ startedAt, duration, windowEnd }: PendingVerification) {
    // Re-read the flag on expiry, not just on scheduling. Timers armed while
    // it was on outlive it by verificationDelayMS, so a kill switch thrown
    // during a bad rollout would otherwise keep emitting for that long.
    if (!this.enabled) return

    const confirmed = this.serverConfirmations.some(
      (at) => at >= startedAt && at <= windowEnd
    )

    if (confirmed) {
      // Audio is reaching the server. Clear any escalation in progress and, if
      // we had previously flagged a problem, record the recovery.
      if (this.hasPendingProblem) {
        this.hasPendingProblem = false
        this.meeting.logEvent('meeting_user_spoke_confirmed_after_problem')
      }
      this.meeting.localCommands.emit('speechReachabilitySuccess')
      return
    }

    // Local mic heard the user but the server never did.
    this.hasPendingProblem = true
    this.meeting.logEvent('meeting_user_spoke_not_confirmed', { duration })
    this.meeting.localCommands.emit('speechReachabilityProblem')
  }
}
